import { IonContent, isPlatform } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { FormInput } from '@/components/FormInput';
import { InfoProtection } from '@/components/InfoProtection';
import { UiTypography } from '@/components/ui/Typography';
import { useCreateStripeClient } from '@/hooks/useCreateStripeClient';
import { amplitudeModule } from '@/modules/amplitude';
import { ApiBotType } from '@/modules/api';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { PhotosTile } from '@/pages/Onboarding/components/PhotoTile';
import { OnboardingTitle } from '@/pages/Onboarding/components/Title';
import { useOnboarding } from '@/pages/Onboarding/context';
import { useQueryPersonalizedAis } from '@/pages/Onboarding/hooks/useQueryPersonalizedAis';
import { checkIsValidateEmail } from '@/pages/Onboarding/utils/checkIsValidEmail';
import { sliceTwins } from '@/pages/Onboarding/utils/sliceTwins';
import { canLogEvents } from '@/utils/canLogEvents';

import { useOwnAiPreloadImages } from '../../../OwnAI/hooks/useOwnAiPreloadImages';
import { layoutQueryParams } from '../../params';
import { LayoutDataType } from '../../types';

import { EmailStyles } from './Email.styles';

export const ScreenEmail: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Email',
      });
    }

    layoutQueryParams.removeStepsData();
  }, []);

  useOwnAiPreloadImages();

  const form = useFormContext<LayoutDataType>();

  const inputRef = React.useRef<HTMLIonInputElement>(null);

  const formCharacters = form.watch('characters');
  const formEmail = form.watch('email');

  const isValidEmail = checkIsValidateEmail(formEmail);

  const { createStripeClient, isCreatingStripeClient } =
    useCreateStripeClient();

  React.useEffect(() => {
    if (isPlatform('android') || isPlatform('desktop')) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setFocus();
        }
      });
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setFocus();
        }
      }, 1000);
    }
  }, []);

  const [twinsData, setTwinsData] = React.useState<ApiBotType[]>(
    formCharacters || [],
  );
  const sicedTwins = sliceTwins(twinsData || []);

  useQueryPersonalizedAis({
    enabled: !twinsData.length || !formCharacters.length,
    form,
    onSuccess: (data) => {
      setTwinsData(data.twins);
    },
  });

  const { wizard } = useOnboarding();

  const handleNextStep = async (): Promise<void> => {
    if (!formEmail || !isValidEmail) return;

    try {
      const lowerCaseEmail = formEmail.toLowerCase().trim();
      await createStripeClient(lowerCaseEmail);

      wizard.nextStep();
    } catch (error) {
      console.error('Can not create a stripe user', error);
    }
  };

  return (
    <EmailStyles>
      <OnboardingNavigation />

      <IonContent>
        <OnboardingTitle className="onboarding-email-title">
          We’ve added{' '}
          <UiTypography variant="inherit" color="danger" component="span">
            1578
          </UiTypography>{' '}
          AI characters
          <br />
          customized to your preferences
        </OnboardingTitle>

        <PhotosTile slicedAis={sicedTwins} />

        <OnboardingTitle className="email-title">
          Enter your email to create a personal account
        </OnboardingTitle>

        <div className="email-input-container">
          <FormInput
            form={form}
            hidden={false}
            onSubmit={handleNextStep}
            disableButton={!isValidEmail}
            ref={inputRef}
            name="email"
            type="email"
            placeholder="Type your email"
            loading={isCreatingStripeClient}
          />
        </div>
        <div className="email-protection">
          <InfoProtection />
        </div>
      </IonContent>
    </EmailStyles>
  );
};
